import CloseIcon from "@mui/icons-material/Close"
import ThermostatIcon from "@mui/icons-material/Thermostat"
import {
  Alert,
  Box,
  Button,
  Dialog,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Locker } from "shared/locker"
import { usePutTemperatureDemoDoorsMutation } from "src/features/things/thingsApi"
import { temperaturePillSx } from "./DoorTemperaturePill"
import { formatTemperature } from "./temperatureDemoFormat"
import { temperatureDemoDoorList } from "./temperatureDemoDoors"
import { temperatureAt, temperatureLevel } from "./temperatureDemoGenerator"
import { TemperatureDemoMiniMap } from "./TemperatureDemoMiniMap"
import { SCENARIO_LOOK } from "./temperatureDemoScenarios"
import {
  activeTemperatureDemoDoors,
  TEMPERATURE_DEMO_CHOICES,
  TemperatureDemoChoice,
  TemperatureDemoScenario,
} from "./temperatureDemoTypes"
import { useDemoNow } from "./useDemoNow"

const eyebrowSx = {
  fontSize: "0.66rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
}

type Props = {
  open: boolean
  onClose: () => void
  locker: Locker
  lockerName: string
  /** Door to select first (e.g. the one open in the drawer). */
  initialDoorId?: string
}

/**
 * LOCKER-534 — the tenant user's demo control surface: pick a door on the
 * mini map, pick its scenario. Every change is a full-map PUT, so it applies
 * at once for every user of the locker.
 */
export const TemperatureDemoDialog: FC<Props> = ({ open, onClose, locker, lockerName, initialDoorId }) => {
  const { t, i18n } = useTranslation()
  const now = useDemoNow()
  const [putDoors, { isLoading, isError, error }] = usePutTemperatureDemoDoorsMutation()
  // 409 = a super admin switched the demo off meanwhile; anything else is generic.
  const errorKey =
    (error as { status?: number } | undefined)?.status === 409
      ? "temperatureDemo.saveErrorDisabled"
      : "temperatureDemo.saveError"
  const demoDoors = activeTemperatureDemoDoors(locker)

  // LOCKER-535 — V1 (`sstLocker`, virtual included) or V2 (`sst2Locker`) doors.
  const doors = useMemo(() => temperatureDemoDoorList(locker), [locker])
  const labelOf = (id: string) => doors.find((d) => d.id === id)?.label ?? id

  const [picked, setPicked] = useState<string | undefined>(initialDoorId)
  const selectedId =
    picked && doors.some((d) => d.id === picked)
      ? picked
      : doors[0]?.id
  const selectedChoice: TemperatureDemoChoice = (selectedId && demoDoors[selectedId]) || "off"
  const demoIds = doors.map((d) => d.id).filter((id) => demoDoors[id])
  const selectedTemp =
    selectedId && selectedChoice !== "off"
      ? temperatureAt(selectedId, selectedChoice, 0, now)
      : undefined

  const save = (next: Record<string, TemperatureDemoScenario>) =>
    putDoors({ lockerId: locker.id, tenantId: locker.tenantId, doors: next })

  const onChoose = (choice: TemperatureDemoChoice) => {
    if (!selectedId) return
    const next = { ...demoDoors }
    if (choice === "off") delete next[selectedId]
    else next[selectedId] = choice
    save(next)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      // On the Paper: it carries role="dialog"; the Dialog root does not.
      slotProps={{
        paper: {
          "data-ui": "UI-SL2W-292",
          "aria-label": t("temperatureDemo.dialogAria", { locker: lockerName }),
          sx: { borderRadius: 4 },
        } as any,
      }}
    >
      <Stack
        direction="row"
        spacing={1.75}
        alignItems="flex-start"
        sx={{
          p: "20px 20px 18px 24px",
          bgcolor: "#eef2f8",
          backgroundImage:
            "radial-gradient(circle at 0% 0%, rgba(35,66,133,0.12), transparent 55%), radial-gradient(circle at 100% 0%, rgba(148,210,194,0.15), transparent 50%)",
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2.5,
            flexShrink: 0,
            color: "primary.main",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: "linear-gradient(135deg, rgba(35,66,133,.18), rgba(148,210,194,.20))",
          }}
        >
          <ThermostatIcon fontSize="small" />
        </Box>
        <Stack spacing={0.5} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={eyebrowSx}>
            {t("temperatureDemo.dialogEyebrow", { locker: lockerName })}
          </Typography>
          <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {t("temperatureDemo.dialogTitle")}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
            {t("temperatureDemo.dialogIntro")}
          </Typography>
        </Stack>
        <IconButton aria-label={t("temperatureDemo.close")} onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Only this body scrolls: the header, the demo-door list and the footer stay in view. */}
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="flex-start"
        gap={3}
        sx={{ p: "20px 24px", flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}
      >
        <Stack spacing={1.25} sx={{ flex: "2 1 560px", minWidth: 0 }}>
          <Typography sx={eyebrowSx}>{t("temperatureDemo.stepPickDoor")}</Typography>
          <TemperatureDemoMiniMap
            locker={locker}
            doors={demoDoors}
            selectedDoorId={selectedId}
            onPick={setPicked}
            now={now}
          />
          <Stack direction="row" flexWrap="wrap" columnGap={1.5} rowGap={0.75} sx={{ fontSize: 11.5, color: "text.secondary" }}>
            {TEMPERATURE_DEMO_CHOICES.map((c) => (
              <Stack key={c} direction="row" alignItems="center" spacing={0.625}>
                <Box
                  component="span"
                  aria-hidden
                  sx={{ width: 12, height: 12, borderRadius: "3px", boxSizing: "border-box", bgcolor: SCENARIO_LOOK[c].bg, border: SCENARIO_LOOK[c].border }}
                />
                <span>{t(SCENARIO_LOOK[c].labelKey)}</span>
              </Stack>
            ))}
          </Stack>
        </Stack>

        <Stack spacing={1.25} sx={{ flex: "1 1 320px", minWidth: 0, position: "sticky", top: 0 }}>
          <Typography sx={eyebrowSx} id="temperature-demo-scenario-label">
            {t("temperatureDemo.stepScenario")}
          </Typography>
          {selectedId && (
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                {t("temperatureDemo.door", { door: labelOf(selectedId) })}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {selectedTemp !== undefined && (
                <Box component="span" sx={{ ...temperaturePillSx(temperatureLevel(selectedTemp)), height: 24, fontSize: 12, px: 1 }}>
                  {formatTemperature(selectedTemp, i18n.language)}
                </Box>
              )}
            </Stack>
          )}
          <RadioGroup
            aria-labelledby="temperature-demo-scenario-label"
            value={selectedChoice}
            onChange={(_, v) => onChoose(v as TemperatureDemoChoice)}
            sx={{ gap: 0.75 }}
          >
            {TEMPERATURE_DEMO_CHOICES.map((c) => {
              const on = selectedChoice === c
              const look = SCENARIO_LOOK[c]
              return (
                <FormControlLabel
                  key={c}
                  value={c}
                  disabled={!selectedId || isLoading}
                  control={<Radio size="small" />}
                  sx={{
                    m: 0,
                    minHeight: 48,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2.5,
                    border: on ? "1.5px solid #234285" : "1px solid rgba(0,0,0,.12)",
                    bgcolor: on ? "rgba(35,66,133,.06)" : "#ffffff",
                    "& .MuiFormControlLabel-label": { flexGrow: 1 },
                  }}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          width: 10,
                          height: 28,
                          borderRadius: "3px",
                          flexShrink: 0,
                          boxSizing: "border-box",
                          bgcolor: look.swatch,
                          ...(c === "off" && { border: "1px dashed #9aa1ab" }),
                        }}
                      />
                      <Stack spacing={0.125}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t(look.labelKey)}</Typography>
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t(look.descKey)}</Typography>
                      </Stack>
                    </Stack>
                  }
                />
              )
            })}
          </RadioGroup>
          {isError && <Alert severity="error">{t(errorKey)}</Alert>}
        </Stack>
      </Stack>

      <Stack spacing={1} sx={{ px: 3, py: 1.5, borderTop: "1px solid rgba(0,0,0,.08)" }}>
        <Typography sx={eyebrowSx}>
          {t("temperatureDemo.demoDoorsTitle", { count: demoIds.length })}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {demoIds.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
              {t("temperatureDemo.noDemoDoors")}
            </Typography>
          )}
          {demoIds.map((id) => (
            <Button
              key={id}
              size="small"
              aria-label={t("temperatureDemo.editDoorAria", { door: labelOf(id) })}
              aria-pressed={id === selectedId}
              onClick={() => setPicked(id)}
              startIcon={
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: SCENARIO_LOOK[demoDoors[id]].swatch }} />
              }
              sx={{
                height: 32,
                px: 1.5,
                borderRadius: 4,
                textTransform: "none",
                fontSize: 12.5,
                fontWeight: 600,
                border: id === selectedId ? "1.5px solid #234285" : "1px solid rgba(35,66,133,.25)",
                bgcolor: id === selectedId ? "rgba(35,66,133,.08)" : "#ffffff",
              }}
            >
              {`${labelOf(id)} · ${t(SCENARIO_LOOK[demoDoors[id]].labelKey)}`}
            </Button>
          ))}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        sx={{ px: 3, py: 1.75, borderTop: "1px solid rgba(0,0,0,.08)", bgcolor: "#fafbfd" }}
      >
        <Button
          onClick={() => save({})}
          disabled={isLoading || Object.keys(demoDoors).length === 0}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {t("temperatureDemo.clearAll")}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {t("temperatureDemo.thresholds")}
        </Typography>
        <Button variant="contained" disableElevation onClick={onClose} sx={{ textTransform: "none", fontWeight: 600 }}>
          {t("temperatureDemo.done")}
        </Button>
      </Stack>
    </Dialog>
  )
}
